import { A } from "@solidjs/router";
import { Show, createSignal, onMount, createEffect, createMemo, Switch, Match } from "solid-js";

import { formatLongDate, schemeData, SubscriptionState, RIASRoles } from "~/lib";
import { getByUserQuery } from "~/lib/queries";
import { queryByPK } from "~/_server/dynamoDB";
import { AddApprovedBody } from "./AddApprovedBody";
import { useRIASApp } from "~/_context/UserProvider";

import { House, Building, Factory, GraduationCap } from "lucide-solid";

const icons = {
	House,
	Building,
	Factory,
	GraduationCap
};

export const Subscription = props => {
	const app = useRIASApp();
	const [subscription, setSubscription] = createSignal( props.subscription );
	const [approvedBody, setApprovedBody] = createSignal();

	onMount( async () => {
		if ( !props.subscription && props.subscriptionId ) {
			const s = await getByUserQuery( 'SCHEME', props.scheme.id );

			setSubscription( s.SCHEME );
		}
	} );

	createEffect( async () => {
		if ( subscription().approvedBodyId ) {
			const ab = await queryByPK( `APPROVEDBODY#${subscription().approvedBodyId}`, '' );

			if ( ab && ab.length > 0 ) {
				setApprovedBody( ab[0] );
			}
		}
	} );

	const scheme = () => schemeData( subscription().scheme );

	const Icon = icons[scheme()?.icon] || icons.House;

	return (
		<>
			<Show when={!props.longForm}>
				<div class="grid grid-cols-2 gap-3" onClick={props.onClick} classList={props.classList}>
					<span>{schemeData( subscription().scheme ).name}</span>
					<span>{subscription().name}</span>
					<span class="col-span-2">{approvedBody()?.address?.title ?? '-'}</span>
					<span class="col-span-2 text-xs mt-1.5">Renews {formatLongDate( subscription().expires )}</span>
				</div>
			</Show>

			<Show when={props.longForm}>
				<Show when={subscription().status == SubscriptionState.APPLYING}>
					<div class="text-center bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded relative" role="alert">
						<p>Your application for membership is being validated. This process can take up to 10 working days. You will receive an email as soon as the process is complete.</p>
					</div>
				</Show>

				<Show when={subscription().status != SubscriptionState.APPLYING}>
					<div class="h-auto border-l-16 grow" classList={{ [`border-sub-status-${subscription().status}`]: true }}>
						<div class="p-5 md:p-8">
							<div class="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
								{/*<!-- LEFT -->*/}
								<div class="flex gap-4">
									<div class="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl" classList={{ [`sub-status-${subscription().status}`]: true }}>
										<Icon class="h-8 w-8" />
									</div>

									<div>
										<div class="flex flex-wrap items-center gap-3">
											<h2 class="text-2xl font-semibold text-zinc-900">
												{scheme()?.name}
											</h2>

											{/*}
											<span class="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-700">
												Active Membership
											</span>*/}
										</div>

										<p class="mt-2 max-w-3xl text-base leading-relaxed text-zinc-600">
											{scheme()?.description}
										</p>
									</div>
								</div>

							</div>

							<div class="mt-8 grid gap-6 border-y border-zinc-200 py-6 sm:grid-cols-2 md:grid-cols-3">
								<div>
									<div class="text-sm font-medium text-zinc-500">Certifier ID</div>

									<div class="mt-2 flex items-center gap-2">
										<span class="text-2xl font-semibold tracking-tight text-zinc-900">
											{subscription().name}
										</span>
									</div>
								</div>

								<div>
									<div class="text-sm font-medium text-zinc-500">Status</div>
									<div class="mt-3">
										<span class="inline-flex rounded-full px-3 py-1 text-sm font-medium" classList={{ [`sub-status-${subscription().status}`]: true }}>
											{subscription().status}
										</span>
									</div>
								</div>

								<Show when={subscription().status != SubscriptionState.CPD}>
									<div>
										<div class="text-sm font-medium text-zinc-500">Renewal Date</div>

										<div class="mt-2 text-2xl font-semibold tracking-tight text-zinc-900">
											<Show when={subscription().expires}>{formatLongDate( subscription().expires )}</Show>
											<Show when={!subscription().expires}>-</Show>
										</div>
									</div>
								</Show>
							</div>

							<Show when={subscription().status != SubscriptionState.CPD}>
								<div class="py-6">
									<div class="text-sm font-medium text-zinc-500">Approved Body</div>

									<div class="mt-2 flex items-center gap-2">
										<span class="text-2xl font-semibold tracking-tight text-zinc-900">
											<Show when={subscription()?.pendingAB} fallback={subscription().approvedBodyId ? approvedBody()?.address?.title : '-'}>
												Pending approval of {subscription().pendingAB?.address?.title ?? subscription().pendingAB?.address?.name}
											</Show>
										</span>
									</div>
								</div>
							</Show>
						</div>
					</div>

					<div class="xl:w-125 flex justify-center items-center">
						<SubscriptionActions subscription={subscription()} />
					</div>

					<Show when={app.userIs( RIASRoles.ADMIN )}>
						<SubscriptionAdminActions subscription={subscription()} />
					</Show>
				</Show>
			</Show>
		</>
	);
}


const SubscriptionAdminActions = ( props ) => {
	const [sub] = createSignal( props.subscription );

	return (
		<div class="mt-4 flex flex-col gap-2">
			<button type="button" class="rias bg-red-500 text-white" onClick={async () => {
				if ( !confirm( "Are you sure you want to suspend this subscription? This will prevent the certifier from issuing certificates until the issue is resolved." ) ) {
					return;
				}
			}}>Suspend Subscription</button>

			<button type="button" class="rias bg-red-500 text-white" onClick={async () => {
				if ( !confirm( "Are you sure you want to suspend this subscription? This will prevent the certifier from issuing certificates until the issue is resolved." ) ) {
					return;
				}
			}}>Terminate Subscription</button>
		</div> );
}

const SubscriptionActions = ( props ) => {
	const [sub] = createSignal( props.subscription );

	return (
		<div class="w-[90%] my-4">
			<Switch>
				<Match when={sub().status == SubscriptionState.ASSESSING}>
					<div class="text-center bg-purple-100 border border-purple-400 text-purple-700 px-4 py-3 rounded-lg relative" role="alert">
						<h3 class="text-lg font-bold">Over to you!</h3>

						<p class="my-2">Your next step is to complete the membership assessments. <A href="/dashboard/exams">Click here to begin</A>.</p>
					</div>
				</Match>

				<Match when={sub().status == SubscriptionState.PRACTICAL} >
					<div class="text-center bg-purple-100 border border-purple-400 text-purple-700 px-4 py-3 rounded-lg relative" role="alert">
						<Switch>
							<Match when={sub().scheme == "section-6-non-domestic" || sub().scheme == "section-6-non-domestic-advanced"}>

								<h3 class="text-lg font-bold">Software Test</h3>

								<p class="my-2">The next step towards becoming an Approved Certifier of Design is to either:</p>

								<ul class="list-disc list-inside text-left text-sm">
									<li>
										provide <a href="mailto:rias-regs@rias.org.uk">rias-regs@rias.org.uk</a> with details of an accredited training course which you have completed.
										(Details can be found at
										<a href="https://www.gov.scot/publications/building-standards-approved-energy-assessment-software-guidance/">
											the Scottish Government's website
										</a>).
									</li>
									<li>
										or, undertake the training and practical test provided by Strathclyde
										University which is{" "}
										<a href="https://onlineshop.strath.ac.uk/product-catalogue/engineering-faculty/mechanical-aerospace-engineering/courses/riascibse-certifier-of-design-training">
											available here
										</a>. The university will inform RIAS-regs when you have
										satisfactorily completed their training and tests.
									</li>
								</ul>
							</Match>

							<Match when={sub().scheme == "section-6-domestic"}>
								<h3 class="text-lg font-bold">Practical SAP Test</h3>
								<p>This will be sent to you separately by <a href="https://home-data.co.uk/">HEADs</a>, which administers this part of the competency process.</p>
							</Match>
						</Switch>
					</div>
				</Match>

				<Match when={sub().status == SubscriptionState.REQUIRES_BODY}>
					<div class="text-center bg-lime-100 border border-lime-400 text-lime-700 px-4 py-3 rounded relative" role="alert">
						<h3 class="text-lg font-bold">Your Approved Body</h3>

						<p class="my-2">Your next step is to submit an approved body.</p>

						<AddApprovedBody subscription={sub()} />	
					</div>
				</Match>

				<Match when={sub().status == SubscriptionState.PENDING_BODY}>
					<div class="text-center bg-lime-100 border border-lime-400 text-lime-700 px-4 py-3 rounded relative" role="alert">
						<h3 class="text-lg font-bold">Thank you for submitting your Approved Body</h3>

						<p class="my-2">Verification is underway, and we will be in touch shortly.</p>
					</div>
				</Match>

				<Match when={sub().status == SubscriptionState.SUSPENDED} >
					<div class="text-center bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
						<h3 class="text-lg font-bold">Subscription Suspended</h3>

						<p class="my-3">Your subscription has been suspended. If you have not received an email regarding this matter, please <a href="mailto:support@rias-regs.co.uk">contact Support</a>.</p>
					</div>
				</Match>

				<Match when={sub().status == SubscriptionState.RENEWING} >
					<div class="text-center bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">
						<h3 class="text-lg font-bold">Subscription Renewing</h3>

						<p class="my-3">Your subscription has expired, but can be renewed by paying the renewal fee. If you have not received an email regarding renewal, please <a href="mailto:support@rias-regs.co.uk">contact Support</a>.</p>

						<p class="my-3">You must not issue certificates until your subscription is renewed.</p>
					</div>
				</Match>

				<Match when={sub().status == SubscriptionState.PENDING} >
					<div class="text-center bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">
						<Switch>
							<Match when={sub().scheme.toLowerCase() == "cpd"} >
								<h3 class="text-lg font-bold">Your Joining Fee is due</h3>

								<p class="my-3">Your details have now been verified and you are invited to join the CPD scheme. You must pay your joining fee to access the learning materials and
									online competency tests.</p>
							</Match>
							<Match when={sub().scheme.toLowerCase() != "cpd"} >
								<h3 class="text-lg font-bold">Your Joining and Subscription Fees are due</h3>

								<p class="my-3">Your details have now been verified and you are invited to join the scheme. You must pay your joining fee and first year's subscription to access the
									online competency tests.</p>
							</Match>
						</Switch>
						<p class="my-3">Invoices can be found on your <a href="/dashboard/account" class="italic font-bold">account page</a>.</p>
					</div>
				</Match>

				<Match when={sub().status == SubscriptionState.EXPIRED} >
					<div class="text-center bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
						<h3 class="text-lg font-bold">Subscription Expired</h3>

						<p class="my-3">Your subscription has expired. If you have not received an email regarding renewal, please <a href="mailto:support@rias-regs.co.uk">contact Support</a>.</p>

						<p class="my-3">You may no longer issue certificates.</p>
					</div>
				</Match>
			</Switch>
		</div>
	);

}